// Adapted from React Bits Specular Button by David Haz.
// https://reactbits.dev/components/specular-button — see LICENSE.md.
import { useEffect, useRef, type ButtonHTMLAttributes } from 'react';
import { Link, type LinkProps } from 'react-router-dom';
import { Mesh, Program, Renderer, Triangle } from 'ogl';
import { useTheme } from '@/contexts/useTheme';
import { cn } from '@/lib/utils';
import './specular-button.css';

const PAD = 16;

const VERT = `#version 300 es
in vec2 position;
void main() {
  gl_Position = vec4(position, 0.0, 1.0);
}
`;

const FRAG = `#version 300 es
precision highp float;

uniform vec2 uCenter;
uniform vec2 uHalfSize;
uniform float uRadius;
uniform float uAngle;
uniform float uPx;
uniform vec3 uLineColor;
uniform vec3 uBaseColor;
uniform float uIntensity;
uniform float uShineSize;
uniform float uShineFade;
uniform float uThickness;
uniform float uBaseWidth;

out vec4 fragColor;

float sdRoundedRect(vec2 p, vec2 b, float r) {
  vec2 q = abs(p) - b + r;
  return length(max(q, 0.0)) + min(max(q.x, q.y), 0.0) - r;
}

float shapeSDF(vec2 p) { return sdRoundedRect(p, uHalfSize, uRadius); }

float gaussianLine(float d, float sigma) {
  float x = d / (sigma + 1e-6);
  float k = mix(1.0, 1.6, smoothstep(0.0, 1.5, x));
  return exp(-k * x * x);
}

void main() {
  vec2 p = gl_FragCoord.xy - uCenter;
  float d = shapeSDF(p);
  vec2 L = vec2(cos(uAngle), sin(uAngle));

  // Dark base stroke hugging the edge for a sense of thickness
  float base = (1.0 - smoothstep(0.0, uBaseWidth, abs(d))) * 0.45;

  // Symmetric specular: the edges facing toward/away from the light both
  // catch a streak. The angular window (size + fade) is measured with an
  // elliptical normal so it varies continuously along straight edges.
  vec2 nEll = normalize(p / (uHalfSize * uHalfSize) + 1e-6);
  float phi = acos(clamp(abs(dot(nEll, L)), 0.0, 1.0));
  float rim = 1.0 - smoothstep(uShineSize - uShineFade, uShineSize + uShineFade + 1e-4, phi);
  float line = gaussianLine(d, uThickness);
  float edgeClamp = 1.0 - smoothstep(0.5 * uPx, 3.0 * uPx, abs(d));
  float hi = line * rim * edgeClamp * uIntensity;

  vec3 col = uBaseColor * base + uLineColor * hi;
  float a = clamp(base + hi, 0.0, 1.0);
  fragColor = vec4(col, a);
}
`;

// The shader reads resolved theme colors rather than maintaining a second palette.
function colorChannels(color: string) {
  return (color.match(/[\d.]+/g) ?? ['255', '255', '255'])
    .slice(0, 3)
    .map(channel => Number(channel) / 255);
}

function SpecularRim({ disabled = false }: { disabled?: boolean }) {
  const fxRef = useRef<HTMLSpanElement>(null);
  const { theme } = useTheme();

  useEffect(() => {
    const fx = fxRef.current;
    const button = fx?.parentElement;
    if (!fx || !button || disabled) return;

    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    let dispose: (() => void) | undefined;

    const setup = () => {
      dispose?.();
      dispose = undefined;
      if (reducedMotion.matches) return;

      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      let renderer: Renderer;
      try {
        renderer = new Renderer({
          alpha: true,
          premultipliedAlpha: true,
          antialias: true,
          dpr,
        });
      } catch {
        // A standard, fully functional button remains when WebGL is unavailable.
        return;
      }
      const gl = renderer.gl;
      if (!renderer.isWebgl2) {
        gl.getExtension('WEBGL_lose_context')?.loseContext();
        return;
      }
      gl.clearColor(0, 0, 0, 0);
      const geometry = new Triangle(gl);
      delete geometry.attributes.uv;
      const program = new Program(gl, {
        vertex: VERT,
        fragment: FRAG,
        transparent: true,
        depthTest: false,
        depthWrite: false,
        uniforms: {
          uCenter: { value: [0, 0] },
          uHalfSize: { value: [1, 1] },
          uRadius: { value: 6 * dpr },
          uAngle: { value: 2.4 },
          uPx: { value: dpr },
          uLineColor: { value: colorChannels(getComputedStyle(fx).color) },
          uBaseColor: {
            value: colorChannels(getComputedStyle(button).backgroundColor),
          },
          uIntensity: { value: 0 },
          uShineSize: { value: (10 * Math.PI) / 180 },
          uShineFade: { value: (40 * Math.PI) / 180 },
          uThickness: { value: dpr },
          uBaseWidth: { value: dpr },
        },
      });
      program.setBlendFunc(gl.ONE, gl.ONE_MINUS_SRC_ALPHA);
      const mesh = new Mesh(gl, { geometry, program });
      fx.appendChild(gl.canvas);

      let angle = 2.4;
      let targetAngle = 2.4;
      let brightness = 0;
      let proximity = 0;
      let visible = true;
      let frame = 0;
      let last = performance.now();

      // Render only while the light is changing; no permanent animation loop.
      const draw = (now: number) => {
        frame = 0;
        const dt = Math.min((now - last) / 1000, 0.05);
        last = now;
        const diff =
          ((targetAngle - angle + Math.PI * 3) % (Math.PI * 2)) - Math.PI;
        const targetBrightness = Math.max(
          proximity,
          document.activeElement === button ? 0.7 : 0
        );
        angle += diff * (1 - Math.exp(-dt * 7));
        brightness += (targetBrightness - brightness) * (1 - Math.exp(-dt * 8));
        program.uniforms.uAngle.value = angle;
        program.uniforms.uIntensity.value = brightness * 0.85;
        renderer.render({ scene: mesh });
        if (
          Math.abs(diff) > 0.001 ||
          Math.abs(targetBrightness - brightness) > 0.001
        )
          requestDraw();
      };
      const requestDraw = () => {
        if (!frame && visible && !document.hidden)
          frame = requestAnimationFrame(draw);
      };
      const resize = () => {
        const { width, height } = button.getBoundingClientRect();
        renderer.setSize(width + PAD * 2, height + PAD * 2);
        program.uniforms.uCenter.value = [
          (PAD + width / 2) * dpr,
          (PAD + height / 2) * dpr,
        ];
        program.uniforms.uHalfSize.value = [
          Math.max(width / 2, 1) * dpr,
          Math.max(height / 2, 1) * dpr,
        ];
        program.uniforms.uRadius.value =
          parseFloat(getComputedStyle(button).borderRadius) * dpr;
        requestDraw();
      };
      const onPointerMove = (event: PointerEvent) => {
        if (event.pointerType === 'touch' || !visible) return;
        const rect = button.getBoundingClientRect();
        const cx = rect.left + rect.width / 2;
        const cy = rect.top + rect.height / 2;
        const dx = Math.max(
          rect.left - event.clientX,
          0,
          event.clientX - rect.right
        );
        const dy = Math.max(
          rect.top - event.clientY,
          0,
          event.clientY - rect.bottom
        );
        const distance = Math.hypot(dx, dy);
        if (distance === 0) {
          const nx = (event.clientX - cx) / (rect.width / 2);
          const ny = (cy - event.clientY) / (rect.height / 2);
          targetAngle =
            Math.atan2(2 / rect.height, -2 / rect.width) + nx * 0.3 + ny * 0.15;
        } else {
          targetAngle = Math.atan2(cy - event.clientY, event.clientX - cx);
        }
        const t = Math.max(0, 1 - distance / 140);
        proximity = t * t * (3 - 2 * t);
        requestDraw();
      };
      const onPointerOut = (event: PointerEvent) => {
        if (!event.relatedTarget) {
          proximity = 0;
          requestDraw();
        }
      };
      const onVisibilityChange = () => {
        if (document.hidden || !visible) {
          cancelAnimationFrame(frame);
          frame = 0;
        } else requestDraw();
      };
      const resizeObserver = new ResizeObserver(resize);
      resizeObserver.observe(button);
      const intersectionObserver = new IntersectionObserver(([entry]) => {
        visible = entry.isIntersecting;
        onVisibilityChange();
      });
      intersectionObserver.observe(button);
      window.addEventListener('pointermove', onPointerMove, { passive: true });
      window.addEventListener('pointerout', onPointerOut);
      document.addEventListener('visibilitychange', onVisibilityChange);
      button.addEventListener('focus', requestDraw);
      button.addEventListener('blur', requestDraw);
      resize();

      dispose = () => {
        cancelAnimationFrame(frame);
        resizeObserver.disconnect();
        intersectionObserver.disconnect();
        window.removeEventListener('pointermove', onPointerMove);
        window.removeEventListener('pointerout', onPointerOut);
        document.removeEventListener('visibilitychange', onVisibilityChange);
        button.removeEventListener('focus', requestDraw);
        button.removeEventListener('blur', requestDraw);
        geometry.remove();
        program.remove();
        gl.canvas.remove();
        gl.getExtension('WEBGL_lose_context')?.loseContext();
      };
    };

    setup();
    reducedMotion.addEventListener('change', setup);
    return () => {
      reducedMotion.removeEventListener('change', setup);
      dispose?.();
    };
  }, [disabled, theme]);

  return (
    <span ref={fxRef} className="specular-button__fx" aria-hidden="true" />
  );
}

const specularClasses =
  'specular-button relative isolate inline-flex h-11 items-center justify-center rounded-md bg-primary px-5 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:pointer-events-none disabled:opacity-50';

export function SpecularButton({
  children,
  className,
  disabled,
  type = 'button',
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button
      {...props}
      type={type}
      disabled={disabled}
      className={cn(specularClasses, className)}
    >
      <SpecularRim disabled={disabled} />
      <span className="specular-button__label">{children}</span>
    </button>
  );
}

export function SpecularLink({ children, className, ...props }: LinkProps) {
  return (
    <Link {...props} className={cn(specularClasses, className)}>
      <SpecularRim />
      <span className="specular-button__label">{children}</span>
    </Link>
  );
}
